import {
  getDistance,
  getOffset,
  movePoint2D,
} from '@shared/helpers/geometry/coordinates.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Point2D } from '@shared/models/geometry.model';
import {
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';

type PayloadPosition = {
  /**
   * Position relative to the target elements offset
   */
  relative: Point2D;

  /**
   * Position relative to browser window [0,0]
   */
  absolute: Point2D;
};

export type ObservableMouseEventPayload = {
  /**
   * The original event
   */
  event: MouseEvent;
  /**
   * The origin position where the interaction started (e.g., mousedown)
   */
  origin: PayloadPosition;

  /**
   * Target offset related to the window is the target it's [0,0]
   */
  targetOffset: Point2D;

  /**
   * The current position relative to the provided target
   */
  position: PayloadPosition;

  /**
   * The difference between the current and original position of the pointer in pixels
   */
  offset: Point2D;

  /**
   * The distance between the current and original position of the pointer in pixels
   */
  distance: number;
};

export type ObservableKeyPressedMouseDownMovePayload =
  ObservableMouseEventPayload & {
    key: string;
  };

/**
 * Get an enriched payload to simplify calculations
 * @param event
 * @param absoluteOrigin
 * @returns
 */
const getPayload = (
  event: MouseEvent,
  absoluteOrigin?: Point2D,
): ObservableMouseEventPayload => {
  const position: PayloadPosition = {
    relative: [event.offsetX, event.offsetY],
    absolute: [event.pageX, event.pageY],
  };
  absoluteOrigin = absoluteOrigin ?? position.absolute;
  const targetOffset = getOffset(position.relative, position.absolute);
  const offset = getOffset(absoluteOrigin, position.absolute);
  const distance = getDistance(absoluteOrigin, position.absolute);

  const origin: PayloadPosition = {
    relative: movePoint2D(absoluteOrigin, [-targetOffset[0], -targetOffset[1]]),
    absolute: absoluteOrigin,
  };
  return {
    position,
    origin,
    targetOffset,
    offset,
    distance,
    event,
  };
};

const getEvent = (
  maybeEvent: MouseEvent | ObservableMouseEventPayload,
): MouseEvent => (guards.is.event(maybeEvent) ? maybeEvent : maybeEvent.event);

/**
 * Helper to filter events by key
 * @param key
 * @returns
 */
const filterByKey = (key: string) => {
  return filter((e: KeyboardEvent) => {
    const eventKey = guards.is.string(e) ? e : e?.key;
    return eventKey === key;
  });
};

const filterByCmdDown = () => {
  return filter(
    (maybeEvent: MouseEvent | ObservableMouseEventPayload) =>
      getEvent(maybeEvent).metaKey,
  );
};

const filterByShiftDown = () => {
  return filter(
    (maybeEvent: MouseEvent | ObservableMouseEventPayload) =>
      getEvent(maybeEvent).shiftKey,
  );
};

const filterByAltDown = () => {
  return filter(
    (maybeEvent: MouseEvent | ObservableMouseEventPayload) =>
      getEvent(maybeEvent).altKey,
  );
};

export const observableFilters = {
  byKey: filterByKey,
  byCmd: filterByCmdDown,
  byShift: filterByShiftDown,
  byAlt: filterByAltDown,
};

/**
 * Create observables for a target element
 * @param target The target element to create observables for
 * @param $destroy Provide a destroy subject to unsubscribe from events when the subject is nexted. Will default to a new subject if not provided (which will never be destroyed)
 * @param eventFilter Predicate controlling which native events should emit
 * @returns An object with the observables for the target element
 */
export const createObservables = (
  target: HTMLElement | Window = window,
  $destroy: Subject<void> = new Subject<void>(),
  eventFilter: (event: MouseEvent | KeyboardEvent) => boolean = () => true,
) => {
  const shouldHandleKeyboardEvent = (event: KeyboardEvent): boolean =>
    eventFilter(event);
  const shouldHandleMouseEvent = (event: MouseEvent): boolean =>
    eventFilter(event);

  // Keyboard events
  const keyDown$ = fromEvent<KeyboardEvent>(window, 'keydown').pipe(
    filter(shouldHandleKeyboardEvent),
    takeUntil($destroy),
  );
  const keyUp$ = fromEvent<KeyboardEvent>(window, 'keyup').pipe(
    filter(shouldHandleKeyboardEvent),
    takeUntil($destroy),
  );
  const keyPressed$ = merge(keyDown$, keyUp$).pipe(
    map((event) => (event.type === 'keydown' ? event.key : undefined)),
    startWith(undefined),
    distinctUntilChanged(),
    shareReplay(1),
    takeUntil($destroy),
  );

  // Mouse events
  const mouseDown$ = fromEvent<MouseEvent>(target, 'mousedown').pipe(
    filter(shouldHandleMouseEvent),
    map((event) => getPayload(event)),
    takeUntil($destroy),
  ); // Target specific
  const mouseMove$ = fromEvent<MouseEvent>(window, 'mousemove').pipe(
    filter(shouldHandleMouseEvent),
    map((event) => getPayload(event)),
    takeUntil($destroy),
  );
  const mouseUp$ = fromEvent<MouseEvent>(window, 'mouseup').pipe(
    map((event) => getPayload(event)),
    takeUntil($destroy),
  );
  const mouseClick$ = fromEvent<MouseEvent>(target, 'click').pipe(
    filter(shouldHandleMouseEvent),
    map((event) => getPayload(event)),
    takeUntil($destroy),
  );

  // Mouse down move
  const mouseDownMove$ = mouseDown$.pipe(
    switchMap((payload) =>
      mouseMove$.pipe(
        map(({ event }) => getPayload(event, payload.origin.absolute)),
      ),
    ),
    takeUntil(mouseUp$),
  );

  const keyPressedMouseDownMove$ = keyPressed$.pipe(
    filter((key) => guards.is.string(key)),
    switchMap((key) =>
      mouseDownMove$.pipe(
        map((payload): ObservableKeyPressedMouseDownMovePayload => ({
          ...payload,
          key,
        })),
      ),
    ),
  );

  return {
    key: {
      down$: keyDown$,
      up$: keyUp$,

      /**
       * Get the key that is currently pressed or undefined if no key is pressed
       */
      pressed$: keyPressed$,
      pressedMouseDownMove$: keyPressedMouseDownMove$,
    },
    mouse: {
      move$: mouseMove$,
      up$: mouseUp$,
      down$: mouseDown$,
      downMove$: mouseDownMove$,
      click$: mouseClick$,
    },
  };
};

export const globalObservables = createObservables();
